import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

const getFontSize = (variant: string) => {
  switch (variant) {
    case 'h1':
      return '3.5rem';
    case 'h2':
      return '3rem';
    case 'h3':
      return '2.5rem';
    case 'h4':
      return '1.125rem';
    case 'subtitle1':
      return '1.5rem';
    case 'subtitle2':
      return '1.25rem';
    case 'body1':
      return '1rem';
    case 'body2':
      return '0.875rem';
    default:
      return '1rem';
  }
};

const getMobileFontSize = (variant: string) => {
  switch (variant) {
    case 'h1':
      return '2.5rem';
    case 'h2':
      return '2rem';
    case 'h3':
      return '1.75rem';
    case 'h4':
      return '1rem';
    case 'subtitle1':
      return '1.25rem';
    case 'subtitle2':
      return '1rem';
    case 'body1':
      return '0.875rem';
    case 'body2':
      return '0.75rem';
    default:
      return '1rem';
  }
};

const get4KFontSize = (variant: string) => {
  switch (variant) {
    case 'h1':
      return '4.5rem';
    case 'h2':
      return '4rem';
    case 'h3':
      return '3.5rem';
    case 'h4':
      return '1.375rem';
    case 'subtitle1':
      return '2rem';
    case 'subtitle2':
      return '1.75rem';
    case 'body1':
      return '1.25rem';
    case 'body2':
      return '1rem';
    default:
      return '1rem';
  }
};

const getColor = (variant: string): string => {
  switch (variant) {
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
      return 'var(--primary-text-color)';
    case 'subtitle1':
    case 'subtitle2':
      return 'var(--secondary-text-color)';
    case 'body1':
    case 'body2':
      return 'var(--secondary-text-color)';
    default:
      return 'var(--primary-text-color)';
  }
};

interface StyledTextProps {
  lineHeight?: number | string;
  letterSpacing?: number | string;
  width?: string;
  bold?: boolean;
}

// !important here overrides MUIs high specificity rules in text components.
// shouldForwardProp keeps `bold` from reaching Typography's underlying DOM
// node - it's not a real Typography/HTML prop, and MUI's styled() (unlike
// styled-components' $-prefix convention) forwards everything by default
// when wrapping a component, so a boolean here would otherwise trigger
// React's "non-boolean attribute" warning on the rendered <p>.
const StyledText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'bold',
})<StyledTextProps>`
  font-family: var(--default-fonts);
  font-weight: ${({ variant, bold }) =>
    bold ||
    variant === 'h1' ||
    variant === 'h2' ||
    variant === 'h3' ||
    variant === 'h4'
      ? '700 !important'
      : '400 !important'};
  font-size: ${({ variant }) => getMobileFontSize(variant!)} !important;
  color: ${({ color, variant }) => color || getColor(variant as string)};
  line-height: ${({ lineHeight }) => lineHeight || 1.75};
  letter-spacing: ${({ letterSpacing }) => letterSpacing || '0.00938em'};
  width: ${({ width }) => width || 'revert'};

  @media (min-width: 769px) {
    font-size: ${({ variant }) => getFontSize(variant!)} !important;
  }

  @media (min-width: 1441px) {
    font-size: ${({ variant }) => get4KFontSize(variant!)} !important;
  }
`;

export default StyledText;
